import React, { useEffect, useState } from "react";
import {
  CHECK_IN_FLAGGED_ISSUE_OPTIONS,
  CHECK_IN_STATUS_OPTIONS,
  CHECK_IN_TYPE_OPTIONS,
  defaultMaxStartDateRangeDate,
  defaultStartDateRangeDate,
  defaultDateRange
} from "src/helpers";
import { WarehouseCheckInsFilters, DateTimeRange } from "src/types";
import { ActiveAntsWarehouseCheckInColumnHeader, TimestampSortOptions, CheckInsGroupsOptions } from "src/enums";
import { DropdownField } from "src/components/ui/inputs";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { Badge } from "src/shadcn/ui/badge";
import { Trash2 } from "lucide-react";

interface FilterActiveAntsWarehouseCheckInsProps {
  onFilterChange: (filters: WarehouseCheckInsFilters | undefined) => void;
  currentFilter?: WarehouseCheckInsFilters;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  sortOption: TimestampSortOptions;
  onSortChange: (option: TimestampSortOptions) => void;
  groupOption?: CheckInsGroupsOptions;
  onGroupChange: (option: CheckInsGroupsOptions) => void;
  columnsToHide: ActiveAntsWarehouseCheckInColumnHeader[];
  onColumnsToHideChange: (columns: ActiveAntsWarehouseCheckInColumnHeader[]) => void;
  allColumns: ActiveAntsWarehouseCheckInColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
}

export const FilterActiveAntsWarehouseCheckIns: React.FC<FilterActiveAntsWarehouseCheckInsProps> = ({
  onFilterChange,
  currentFilter,
  dateTimeRange,
  onDateTimeRangeChange,
  sortOption,
  onSortChange,
  groupOption = CheckInsGroupsOptions.NONE,
  onGroupChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen
}) => {
  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localSortOption, setLocalSortOption] = useState<TimestampSortOptions>(sortOption);
  const [localGroupOption, setLocalGroupOption] = useState<CheckInsGroupsOptions>(groupOption);

  const [filters, setFilters] = useState<Partial<WarehouseCheckInsFilters>>({
    checkInTypeFilter: currentFilter?.checkInTypeFilter,
    checkInStatusFilter: currentFilter?.checkInStatusFilter,
    checkInFlaggedIssueFilter: currentFilter?.checkInFlaggedIssueFilter
  });

  useEffect(() => {
    if (isOpen) {
      setLocalDateTimeRange(dateTimeRange);
      setLocalSortOption(sortOption);
      setLocalGroupOption(groupOption);
      setFilters({
        checkInTypeFilter: currentFilter?.checkInTypeFilter,
        checkInStatusFilter: currentFilter?.checkInStatusFilter,
        checkInFlaggedIssueFilter: currentFilter?.checkInFlaggedIssueFilter
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleFilterChange = <K extends keyof WarehouseCheckInsFilters>(key: K, value: WarehouseCheckInsFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const updatedFilters: WarehouseCheckInsFilters = {
      ...filters,
      filterPopoverVisible: false
    } as WarehouseCheckInsFilters;

    onDateTimeRangeChange(localDateTimeRange);
    onSortChange(localSortOption);
    onGroupChange(localGroupOption);
    onFilterChange(updatedFilters);
    onClose?.();
  };

  const handleClearAllFilters = () => {
    const clearedFilters: WarehouseCheckInsFilters = {
      checkInTypeFilter: undefined,
      checkInStatusFilter: undefined,
      checkInFlaggedIssueFilter: undefined,
      filterPopoverVisible: false
    };

    const defaultRange = defaultDateRange();

    setFilters(clearedFilters);
    setLocalDateTimeRange(defaultRange);
    setLocalSortOption(TimestampSortOptions.TIMESTAMP_DATE_DESC);
    setLocalGroupOption(CheckInsGroupsOptions.NONE);

    onFilterChange(clearedFilters);
    onDateTimeRangeChange(defaultRange);
    onSortChange(TimestampSortOptions.TIMESTAMP_DATE_DESC);
    onGroupChange(CheckInsGroupsOptions.NONE);
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.checkInTypeFilter) count++;
    if (filters.checkInStatusFilter) count++;
    if (filters.checkInFlaggedIssueFilter) count++;
    if (localDateTimeRange.start) {
      const defaultStart = defaultStartDateRangeDate();
      const start = new Date(localDateTimeRange.start);
      const isDefault =
        start.getFullYear() === defaultStart.getFullYear() &&
        start.getMonth() === defaultStart.getMonth() &&
        start.getDate() === defaultStart.getDate() &&
        start.getHours() === defaultStart.getHours() &&
        start.getMinutes() === defaultStart.getMinutes();
      if (!isDefault) count++;
    }
    if (localDateTimeRange.end) count++;
    if (localSortOption !== TimestampSortOptions.TIMESTAMP_DATE_DESC) count++;
    if (localGroupOption !== CheckInsGroupsOptions.NONE) count++;
    return count;
  };

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const parsed = new Date(date);
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      const hours = String(parsed.getHours()).padStart(2, "0");
      const minutes = String(parsed.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    if (!value) {
      setLocalDateTimeRange((prev) => ({ ...prev, [field]: undefined }));
      return;
    }

    const newDate = new Date(value);
    const maxStartDate = defaultMaxStartDateRangeDate();
    const maxEndDate = new Date();

    setLocalDateTimeRange((prev) => {
      if (field === "start") {
        const maxAllowedStart = prev.end ? new Date(prev.end) : maxEndDate;
        const clampedStart =
          newDate < maxStartDate ? maxStartDate : newDate > maxAllowedStart ? maxAllowedStart : newDate;
        return {
          start: clampedStart,
          end: prev.end && clampedStart > prev.end ? clampedStart : prev.end
        };
      }
      const minAllowedEnd = prev.start ? new Date(prev.start) : maxStartDate;
      const clampedEnd = newDate < minAllowedEnd ? minAllowedEnd : newDate > maxEndDate ? maxEndDate : newDate;
      return { ...prev, end: clampedEnd };
    });
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters() > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters()}
              </Badge>
            )}
          </div>
          {countActiveFilters() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          <DropdownField
            label="Check-in Type"
            value={filters.checkInTypeFilter}
            onChange={(value) => handleFilterChange("checkInTypeFilter", value as never)}
            options={CHECK_IN_TYPE_OPTIONS}
          />

          <DropdownField
            label="Check-in Status"
            value={filters.checkInStatusFilter}
            onChange={(value) => handleFilterChange("checkInStatusFilter", value as never)}
            options={CHECK_IN_STATUS_OPTIONS}
          />

          <DropdownField
            label="Flagged Issues"
            value={filters.checkInFlaggedIssueFilter}
            onChange={(value) => handleFilterChange("checkInFlaggedIssueFilter", value as never)}
            options={CHECK_IN_FLAGGED_ISSUE_OPTIONS}
          />

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Start Date</label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.start)}
              onChange={(e) => handleDateChange("start", e.target.value)}
              min={formatDateForInput(defaultMaxStartDateRangeDate())}
              max={formatDateForInput(localDateTimeRange.end || new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">End Date</label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.end)}
              onChange={(e) => handleDateChange("end", e.target.value)}
              min={formatDateForInput(localDateTimeRange.start || defaultMaxStartDateRangeDate())}
              max={formatDateForInput(new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(TimestampSortOptions.TIMESTAMP_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === TimestampSortOptions.TIMESTAMP_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(TimestampSortOptions.TIMESTAMP_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === TimestampSortOptions.TIMESTAMP_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Group By</label>
            <div className="flex gap-1.5">
              {Object.values(CheckInsGroupsOptions).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocalGroupOption(option)}
                  className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                    localGroupOption === option
                      ? "bg-secondary-900 text-white shadow-sm"
                      : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="sticky -bottom-4 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
          >
            Apply Changes
          </Button>
        </div>
      </div>

      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible
                    ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100"
                    : "bg-transparent text-secondary-400 hover:bg-secondary-50"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge
                  variant={isVisible ? "default" : "outline"}
                  className={`text-xs px-1.5 py-0 h-4 flex-shrink-0 ml-2 ${isVisible ? "bg-green-500 hover:bg-green-600" : ""}`}
                >
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
